import { InfosecLevel } from "@istari/istari-client";
import { useMemo } from "react";

import { useGetPlatformInfosecSettings, useListInfosecLevels } from "~/api/useInfosecLevels";
import { useUsers } from "~/user/data-source/use-users";
import { useCurrentUserIdentity } from "~/user/feature/use-current-user-identity";
import { findCurrentRegistryUser } from "~/user/model/current-user-identity";

interface UseAllowedInfosecLevelsOptions {
  ignoreUserLevel?: boolean;
  enabled?: boolean;
}

/** Returns infosec levels the current user is allowed to assign, filtered by platform and optional constraints. */
export function useAllowedInfosecLevels(
  systemInfosecLevel?: InfosecLevel | null,
  options?: UseAllowedInfosecLevelsOptions,
) {
  const identity = useCurrentUserIdentity();
  const platformSettingsQuery = useGetPlatformInfosecSettings();
  const infosecLevelsQuery = useListInfosecLevels({ enabled: options?.enabled });
  const orgUsersQuery = useUsers();

  return useMemo(() => {
    const levels = infosecLevelsQuery.data;
    if (!levels) return [];
    const sorted = [...levels].sort((a, b) => a.order - b.order);

    const platformMaxLevelOrder = platformSettingsQuery.data?.maxLevel?.order;
    const systemMaxLevelOrder = systemInfosecLevel?.order;

    const currentUser = findCurrentRegistryUser(orgUsersQuery.data, identity);
    const userAssignedLevelOrder = options?.ignoreUserLevel ? undefined : currentUser?.infosecLevel?.order;

    const constraints = [platformMaxLevelOrder, systemMaxLevelOrder, userAssignedLevelOrder].filter(
      (order): order is number => order !== undefined && order !== null,
    );

    if (constraints.length === 0) return sorted;

    const effectiveMaxOrder = Math.min(...constraints);

    return sorted.filter((level) => level.order <= effectiveMaxOrder);
  }, [
    infosecLevelsQuery.data,
    platformSettingsQuery.data?.maxLevel?.order,
    systemInfosecLevel,
    identity,
    orgUsersQuery.data,
    options?.ignoreUserLevel,
  ]);
}
